import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import SchemaMarkup from './components/SchemaMarkup';
import RichSnippets from './components/RichSnippets';
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import PolicyPage from './pages/PolicyPage';
import NotFound from './pages/NotFound';
import Services from './pages/Services';
import Testimonials from './pages/Testimonials';
import CaseStudies from './pages/CaseStudies';
import QuantumFrameHeader from './components/QuantumFrameHeader';
import Footer from './components/Footer';
import AnimatedLoading from './components/AnimatedLoading';
import CursorAnimation from './utils/CursorAnimation';
import WhatsAppChat from './components/WhatsAppChat';
import SEOOptimization from './components/SEOOptimization';
import { seoConfig } from './utils/seoConfig';
import './styles/CursorAnimation.css';
import './styles/App.css';

// SEO Analytics Component
const SEOAnalytics = () => {
  useEffect(() => {
    // Google Analytics 4 - Already loaded in HTML head
    // Additional configuration for SPA tracking
    if (window.gtag) {
      window.gtag('config', 'G-9CGR8QQ3PN', {
        page_title: document.title,
        page_location: window.location.href,
        send_page_view: true
      });
    }

    // Google Search Console verification
    const searchConsoleScript = document.createElement('meta');
    searchConsoleScript.name = 'google-site-verification';
    // searchConsoleScript.content = 'YOUR_SEARCH_CONSOLE_VERIFICATION_CODE';
    document.head.appendChild(searchConsoleScript);

    // Bing Webmaster Tools verification
    const bingScript = document.createElement('meta');
    bingScript.name = 'msvalidate.01';
    // bingScript.content = 'YOUR_BING_VERIFICATION_CODE';
    document.head.appendChild(bingScript);

    return () => {
      // Cleanup scripts
      if (document.head.contains(searchConsoleScript)) document.head.removeChild(searchConsoleScript);
      if (document.head.contains(bingScript)) document.head.removeChild(bingScript);
    };
  }, []);

  return null;
};
// Page tracking component for analytics
const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page views
    if (window.gtag) {
      window.gtag('config', 'G-9CGR8QQ3PN', {
        page_path: location.pathname,
        page_title: document.title,
        page_location: window.location.href
      });
    }

    // Update canonical URL dynamically
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${seoConfig.baseUrl}${location.pathname}`;

    // Add page-specific structured data
    const pageStructuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${seoConfig.baseUrl}${location.pathname}#webpage`,
      "url": `${seoConfig.baseUrl}${location.pathname}`,
      "name": document.title,
      "description": document.querySelector('meta[name="description"]')?.content || seoConfig.defaultDescription,
      "isPartOf": {
        "@id": `${seoConfig.baseUrl}/#website`
      },
      "about": {
        "@id": `${seoConfig.baseUrl}/#organization`
      },
      "datePublished": "2020-01-01T00:00:00+05:30",
      "dateModified": new Date().toISOString(),
      "inLanguage": "en-IN",
      "potentialAction": [
        {
          "@type": "ReadAction",
          "target": [`${seoConfig.baseUrl}${location.pathname}`]
        }
      ]
    };

    // Add or update page structured data
    let existingPageScript = document.querySelector('script[data-page-schema]');
    if (existingPageScript) {
      existingPageScript.innerHTML = JSON.stringify(pageStructuredData);
    } else {
      const pageScript = document.createElement('script');
      pageScript.type = 'application/ld+json';
      pageScript.setAttribute('data-page-schema', 'true');
      pageScript.innerHTML = JSON.stringify(pageStructuredData);
      document.head.appendChild(pageScript);
    }

  }, [location]);

  return null;
};

function App() {
  const [isLoading, setIsLoading] = useState(() => {
    // Only show loading on initial page load or refresh, not on navigation
    try {
      // Modern browsers use performance.getEntriesByType('navigation')
      const navigationEntries = performance.getEntriesByType('navigation');
      const isPageRefresh = navigationEntries.length > 0 && 
        (navigationEntries[0].type === 'reload' || navigationEntries[0].type === 'navigate');
      
      // Fallback for older browsers
      const fallbackRefresh = performance.navigation && 
        (performance.navigation.type === performance.navigation.TYPE_RELOAD ||
         performance.navigation.type === performance.navigation.TYPE_NAVIGATE);
      
      const hasSessionStorage = !sessionStorage.getItem('appInitialized');
      
      return (isPageRefresh || fallbackRefresh || hasSessionStorage);
    } catch (error) {
      // If any error occurs, show loading by default
      return !sessionStorage.getItem('appInitialized');
    }
  });

  useEffect(() => {
    // Only run loading animation on initial load or reload
    if (isLoading) {
      // Mark app as initialized in session storage
      sessionStorage.setItem('appInitialized', 'true');
      
      // Simulate loading time with a minimum guaranteed display time
      const startTime = Date.now();
      const minDisplayTime = 3500;

      const loadingTimeout = setTimeout(() => {
        const elapsedTime = Date.now() - startTime;

        if (elapsedTime < minDisplayTime) {
          const remainingTime = minDisplayTime - elapsedTime;
          setTimeout(() => setIsLoading(false), remainingTime);
        } else {
          setIsLoading(false);
        }
      }, 3000);

      return () => {
        clearTimeout(loadingTimeout);
      };
    }
  }, [isLoading]);

  // Enhanced SEO setup on app initialization
  useEffect(() => {
    // Add global structured data for the website
    const websiteSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${seoConfig.baseUrl}/#website`,
          "url": seoConfig.baseUrl,
          "name": seoConfig.siteName,
          "description": seoConfig.defaultDescription,
          "publisher": {
            "@id": `${seoConfig.baseUrl}/#organization`
          },
          "inLanguage": "en-IN",
          "copyrightYear": new Date().getFullYear(),
          "copyrightHolder": {
            "@id": `${seoConfig.baseUrl}/#organization`
          },
          "potentialAction": [
            {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${seoConfig.baseUrl}/search?q={search_term_string}`
              },
              "query-input": "required name=search_term_string"
            }
          ],
          "alternateName": [
            "Quantum Frame Studio",
            "QuantumFrame Studio Bhopal",
            "Quantum Frame Digital Agency"
          ]
        },
        seoConfig.technical.organizationStructuredData
      ]
    };

    // Add website schema
    const websiteScript = document.createElement('script');
    websiteScript.type = 'application/ld+json';
    websiteScript.id = 'website-schema';
    websiteScript.innerHTML = JSON.stringify(websiteSchema);
    document.head.appendChild(websiteScript);

    // Add performance monitoring for Core Web Vitals
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          // Monitor Core Web Vitals for SEO
          if (entry.entryType === 'navigation') {
            const loadTime = entry.loadEventEnd - entry.loadEventStart;
            console.log('Page Load Time:', loadTime);
            
            // Send to analytics if available
            if (window.gtag) {
              window.gtag('event', 'page_load_time', {
                event_category: 'Performance',
                event_label: window.location.pathname,
                value: Math.round(loadTime)
              });
            }
          }
          
          // Monitor Largest Contentful Paint (LCP)
          if (entry.entryType === 'largest-contentful-paint') {
            console.log('LCP:', entry.startTime);
            if (window.gtag) {
              window.gtag('event', 'LCP', {
                event_category: 'Web Vitals',
                event_label: window.location.pathname,
                value: Math.round(entry.startTime)
              });
            }
          }
          
          // Monitor First Input Delay (FID)
          if (entry.entryType === 'first-input') {
            console.log('FID:', entry.processingStart - entry.startTime);
            if (window.gtag) {
              window.gtag('event', 'FID', {
                event_category: 'Web Vitals',
                event_label: window.location.pathname,
                value: Math.round(entry.processingStart - entry.startTime)
              });
            }
          }
        });
      });
      
      observer.observe({ 
        entryTypes: ['navigation', 'largest-contentful-paint', 'first-input'] 
      });
    }

    // Add Cumulative Layout Shift (CLS) monitoring
    if ('PerformanceObserver' in window) {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        }
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      
      // Report CLS when page is hidden
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          console.log('CLS:', clsValue);
          if (window.gtag) {
            window.gtag('event', 'CLS', {
              event_category: 'Web Vitals',
              event_label: window.location.pathname,
              value: Math.round(clsValue * 1000)
            });
          }
        }
      });
    }

    // Add breadcrumb structured data
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'breadcrumb-schema';
    breadcrumbScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": seoConfig.baseUrl
        }
      ]
    });
    document.head.appendChild(breadcrumbScript);

    // Add enhanced meta tags for discoverability
    const metaTags = [
      // Enhanced meta tags for better local SEO with 2024 trending keywords
      { name: 'description', content: 'QuantumFrame Studio - Bhopal\'s #1 web development and digital marketing agency. Expert website design, SEO services, social media marketing, e-commerce development. Proven results: 900% traffic growth, 425% conversion increases. AI-powered solutions, mobile-first design, voice search optimization. Serving Bhopal, Indore, Jabalpur, MP.' },
      { name: 'keywords', content: 'web development bhopal, digital marketing agency bhopal, SEO services bhopal, social media marketing bhopal, website design company bhopal, e-commerce development bhopal, QuantumFrame Studio, web developers in bhopal, digital marketing experts MP, responsive web design agency, conversion rate optimization, Google Ads management, AI-powered web development, progressive web app development, voice search optimization, local SEO bhopal, mobile-first web development, performance marketing agency, growth hacking services madhya pradesh, brand storytelling content marketing, headless CMS development, JAMstack development services, web accessibility compliance, GDPR compliant website development, multilingual website development, B2B digital marketing agency, lead generation website development, sales funnel optimization services, marketing automation setup services, social commerce development, omnichannel marketing strategy, data-driven digital marketing services, customer experience optimization services, digital transformation consulting bhopal' },
      { name: 'author', content: 'QuantumFrame Studio' },
      { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
      { name: 'googlebot', content: 'index, follow' },
      { name: 'bingbot', content: 'index, follow' },
      
      // App-specific meta tags
      { name: 'application-name', content: 'QuantumFrame Studio' },
      { name: 'apple-mobile-web-app-title', content: 'QuantumFrame Studio' },
      { name: 'msapplication-TileColor', content: '#1a1a1a' },
      { name: 'theme-color', content: '#000000' },
      { name: 'mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
      { name: 'format-detection', content: 'telephone=yes' },
      { name: 'format-detection', content: 'address=yes' },
      { name: 'format-detection', content: 'email=yes' },
      
      // Open Graph meta tags
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'QuantumFrame Studio' },
      { property: 'og:locale', content: 'en_IN' },
      { property: 'og:image:type', content: 'image/svg+xml' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      
      // Twitter meta tags
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: '@quantumframestudio' },
      { name: 'twitter:domain', content: 'quantumframestudio.com' },
      
      // Geographic meta tags
      { name: 'geo.region', content: 'IN-MP' },
      { name: 'geo.placename', content: 'Bhopal' },
      { name: 'geo.position', content: '23.2599;77.4126' },
      { name: 'ICBM', content: '23.2599, 77.4126' },
      
      // Additional SEO meta tags
      { name: 'referrer', content: 'strict-origin-when-cross-origin' }
    ];

    metaTags.forEach(tag => {
      let element = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (!element) {
        element = document.createElement('meta');
        if (tag.name) element.setAttribute('name', tag.name);
        if (tag.property) element.setAttribute('property', tag.property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', tag.content);
    });

    // Add link tags for better SEO
    const linkTags = [
      { rel: 'manifest', href: '/manifest.json' },
      { rel: 'shortcut icon', href: '/favicon.ico' },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
      { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
      { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
      { rel: 'mask-icon', href: '/safari-pinned-tab.svg', color: '#000000' },
      { rel: 'preload', href: '/assets/video/heroes-back.mp4', as: 'video', type: 'video/mp4' },
      { rel: 'preload', href: '/assets/video/front-backgroundvid.mp4', as: 'video', type: 'video/mp4' },
      { rel: 'dns-prefetch', href: '//fonts.googleapis.com' },
      { rel: 'dns-prefetch', href: '//cdnjs.cloudflare.com' },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' }
    ];

    linkTags.forEach(tag => {
      const link = document.createElement('link');
      Object.keys(tag).forEach(key => {
        if (key === 'crossOrigin') {
          link.setAttribute('crossorigin', tag[key]);
        } else {
          link.setAttribute(key, tag[key]);
        }
      });
      document.head.appendChild(link);
    });

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + window.location.pathname);

    // Add hreflang for multi-language support
    let hreflang = document.querySelector('link[rel="alternate"][hreflang="en-in"]');
    if (!hreflang) {
      hreflang = document.createElement('link');
      hreflang.setAttribute('rel', 'alternate');
      hreflang.setAttribute('hreflang', 'en-in');
      hreflang.setAttribute('href', window.location.href);
      document.head.appendChild(hreflang);
    }

    return () => {
      // Cleanup on unmount
      const websiteSchemaElement = document.getElementById('website-schema');
      if (websiteSchemaElement) websiteSchemaElement.remove();
      
      const breadcrumbSchemaElement = document.getElementById('breadcrumb-schema');
      if (breadcrumbSchemaElement) breadcrumbSchemaElement.remove();
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <SEOAnalytics />
        <SchemaMarkup />
        <RichSnippets />
        <SEOOptimization />
        <PageTracker />
        
        <div className="app-container">
          {isLoading ? (
            <AnimatedLoading />
          ) : (
            <>
              <CursorAnimation />
              
              {/* Enhanced header with structured data */}
              <header role="banner" itemScope itemType="https://schema.org/Organization">
                <meta itemProp="name" content="QuantumFrame Studio" />
                <meta itemProp="url" content={seoConfig.baseUrl} />
                <meta itemProp="logo" content={`${seoConfig.baseUrl}/assets/images/quantumframe-logo.svg`} />
                <meta itemProp="telephone" content={seoConfig.business.phone} />
                <meta itemProp="email" content={seoConfig.business.email} />
                <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                  <meta itemProp="streetAddress" content="123 IT Park, Zone-II" />
                  <meta itemProp="addressLocality" content="Bhopal" />
                  <meta itemProp="addressRegion" content="Madhya Pradesh" />
                  <meta itemProp="postalCode" content="462022" />
                  <meta itemProp="addressCountry" content="IN" />
                </div>
                <QuantumFrameHeader />
              </header>
              
              {/* Enhanced main content with proper structure */}
              <main className="main-content" role="main" id="main-content">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/contact" element={<ContactUs />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/case-studies" element={<CaseStudies />} />
                  <Route path="/testimonials" element={<Testimonials />} />
                  <Route path="/policy" element={<PolicyPage />} />
                  <Route path="/policies" element={<PolicyPage />} />
                  <Route path="/terms" element={<PolicyPage />} />
                  <Route path="/service-policies" element={<PolicyPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
              
              {/* Enhanced footer with structured data */}
              <footer role="contentinfo" itemScope itemType="https://schema.org/Organization">
                <meta itemProp="name" content="QuantumFrame Studio" />
                <meta itemProp="url" content={seoConfig.baseUrl} />
                <div itemProp="contactPoint" itemScope itemType="https://schema.org/ContactPoint">
                  <meta itemProp="telephone" content={seoConfig.business.phone} />
                  <meta itemProp="contactType" content="customer service" />
                  <meta itemProp="email" content={seoConfig.business.email} />
                  <meta itemProp="availableLanguage" content="English" />
                  <meta itemProp="availableLanguage" content="Hindi" />
                </div>
                <Footer />
              </footer>
              
              {/* Skip to main content for accessibility and SEO */}
              <a href="#main-content" className="skip-link sr-only">
                Skip to main content
              </a>

              {/* WhatsApp Chat Button - Sticky across all pages */}
              <WhatsAppChat />
              
              {/* Accessibility improvements */}
              <div id="live-region" className="sr-only" aria-live="polite" aria-atomic="true"></div>
              
              {/* Schema.org JSON-LD for enhanced SEO */}
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "ProfessionalService",
                    "name": "QuantumFrame Studio",
                    "alternateName": [
                      "Quantum Frame Studio",
                      "QuantumFrame Studio Bhopal",
                      "Quantum Frame Digital Agency"
                    ],
                    "url": seoConfig.baseUrl,
                    "logo": `${seoConfig.baseUrl}/assets/images/quantumframe-logo.svg`,
                    "description": seoConfig.defaultDescription,
                    "serviceType": "Web Development and Digital Marketing",
                    "provider": {
                      "@type": "Organization",
                      "name": "QuantumFrame Studio"
                    },
                    "areaServed": {
                      "@type": "GeoCircle",
                      "geoMidpoint": {
                        "@type": "GeoCoordinates",
                        "latitude": seoConfig.location.coordinates.lat,
                        "longitude": seoConfig.location.coordinates.lng
                      },
                      "geoRadius": "100000"
                    },
                    "offers": {
                      "@type": "AggregateOffer",
                      "offerCount": "6",
                      "lowPrice": "15000",
                      "highPrice": "200000",
                      "priceCurrency": "INR"
                    },
                    "hasOfferCatalog": {
                      "@type": "OfferCatalog",
                      "name": "Digital Services Catalog",
                      "itemListElement": seoConfig.business.services.map((service, index) => ({
                        "@type": "Offer",
                        "itemOffered": {
                          "@type": "Service",
                          "name": service,
                          "provider": {
                            "@type": "Organization",
                            "name": "QuantumFrame Studio"
                          }
                        }
                      }))
                    }
                  })
                }}
              />
            </>
          )}
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;